import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button } from './Button'
import { IconButton } from './IconButton'
import { CloseIcon } from './icons'

export type ToastTone = 'info' | 'success' | 'warn' | 'error'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastProps {
  /** Renders nothing when false, so it can be driven straight off state. Default true. */
  open?: boolean
  message: ReactNode
  tone?: ToastTone
  /** One optional action, e.g. Undo or Reload. Tapping it also dismisses the toast. */
  action?: ToastAction
  /**
   * A quieter second action shown before `action`, e.g. "Wrong line? Change". Tapping it
   * also dismisses the toast.
   */
  secondaryAction?: ToastAction
  onDismiss?: () => void
  /** Auto-dismiss after this many ms. 0 or undefined keeps it up. */
  duration?: number
  className?: string
}

/** One toast, in place. Use `useToast()` for the app-wide queue. */
export function Toast({
  open = true,
  message,
  tone = 'info',
  action,
  secondaryAction,
  onDismiss,
  duration,
  className,
}: ToastProps) {
  const dismiss = useRef(onDismiss)
  dismiss.current = onDismiss

  useEffect(() => {
    if (!open || !duration) return
    const t = setTimeout(() => dismiss.current?.(), duration)
    return () => clearTimeout(t)
  }, [open, duration, message])

  if (!open) return null

  return (
    <div
      className={`rs-toast rs-toast--${tone} ${className ?? ''}`}
      role={tone === 'error' ? 'alert' : 'status'}
      aria-live={tone === 'error' ? 'assertive' : 'polite'}
    >
      <span className="rs-toast__msg">{message}</span>
      {secondaryAction ? (
        <Button
          className="rs-toast__action rs-toast__action--secondary"
          size="sm"
          variant="ghost"
          onClick={() => {
            secondaryAction.onClick()
            dismiss.current?.()
          }}
        >
          {secondaryAction.label}
        </Button>
      ) : null}
      {action ? (
        <Button
          className="rs-toast__action"
          size="sm"
          variant="ghost"
          onClick={() => {
            action.onClick()
            dismiss.current?.()
          }}
        >
          {action.label}
        </Button>
      ) : null}
      {onDismiss ? (
        <IconButton icon={<CloseIcon size={18} />} label="Dismiss" onClick={onDismiss} />
      ) : null}
    </div>
  )
}

// --------------------------------------------------------------- provider

export interface ToastOptions {
  tone?: ToastTone
  /** ms; 0 keeps it up until dismissed. Default 4 s, 6 s with an action. */
  duration?: number
  action?: ToastAction
  /** A quieter second action next to `action`. */
  secondaryAction?: ToastAction
}

export interface ToastApi {
  show: (message: ReactNode, opts?: ToastOptions) => number
  dismiss: (id: number) => void
  /** "Deleted Hotham. Undo" — the standard after any destructive tap. */
  undo: (message: ReactNode, onUndo: () => void | Promise<void>) => number
}

interface Item extends ToastOptions {
  id: number
  message: ReactNode
}

const ToastContext = createContext<ToastApi | null>(null)

let nextId = 1

/**
 * The app-wide toast queue: at most three at once, newest at the bottom, pinned above the
 * bottom tab bar (`--rs-bottom-space`, set by the shell) or above a screen's own fixed footer
 * (a screen sets `--rs-footer-space` on :root while its footer is up).
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Item[]>([])

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const show = useCallback<ToastApi['show']>((message, opts = {}) => {
    const id = nextId++
    setItems((prev) => [...prev.slice(-2), { ...opts, id, message }])
    return id
  }, [])

  const undo = useCallback<ToastApi['undo']>(
    (message, onUndo) => show(message, { action: { label: 'Undo', onClick: () => void onUndo() } }),
    [show],
  )

  const api = useMemo(() => ({ show, dismiss, undo }), [show, dismiss, undo])

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined' && items.length > 0
        ? createPortal(
            <div className="rs-toast-host">
              {items.map((t) => (
                <Toast
                  key={t.id}
                  message={t.message}
                  tone={t.tone ?? 'info'}
                  duration={t.duration ?? (t.action || t.secondaryAction ? 6000 : 4000)}
                  {...(t.action ? { action: t.action } : {})}
                  {...(t.secondaryAction ? { secondaryAction: t.secondaryAction } : {})}
                  onDismiss={() => dismiss(t.id)}
                />
              ))}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  )
}

const NOOP: ToastApi = { show: () => -1, dismiss: () => undefined, undo: () => -1 }

/** No-ops outside a ToastProvider, so a component never breaks for want of a host. */
export function useToast(): ToastApi {
  return useContext(ToastContext) ?? NOOP
}
